import {
  activateAccount,
  attachUserDetails,
  attachUserId,
  logout,
  setRegion,
  signIn,
  updateAuthenticationContext,
} from '../store/actions/auth.actions';
import { ServiceBase } from './service.base';
import AuthenticationServiceApi from '../api/authentication.service.api';
import {
  GENERIC_SERVER_ERROR_MESSAGE,
  ORGANIZATION_UNAUTHORIZED,
  USER_DEACTIVATED,
} from '../constants/applicationErrorCodes';
import { navigate } from './navigation.service';
import { ROUTES, DEVICE_FLAGS, AUTHENTICATION_TYPE, SIGN_OUT_CODES } from '../constants';
import { APPLICATION_STEPS } from '../constants/applicationSteps';
import { finishStep } from '../store/actions/application.actions';
import userServiceApi from '../api/user.service.api';
import API from '../api/API';
import authService from './auth.service';
import { currentEnvironment } from './Environment';
import { switchOrganization, updateOrganization } from '../store/actions/organization.actions';
import authenticationServiceApi from '../api/authentication.service.api';
import _ from 'lodash';
import Logger from '../utils/Logger';

class UserService extends ServiceBase {
  constructor(props) {
    super(props);
    this.reducer = 'auth';
    this.logger = Logger.create({ loggerName: 'UserService' });
  }

  /**
   * Signin user
   * @param {*} { email }
   * @returns
   * @memberof UserService
   */
  async signin({ email, navigateToOtp = true }) {
    try {
      const { region } = await this._selectRegion({
        email,
      });

      if (!region) {
        return { error: 'NO_REGION' };
      }

      this.dispatch(setRegion({ region }));
    } catch (e) {
      return { error: e.message };
    }

    const { error, data } = await AuthenticationServiceApi.loginWithEmail({
      email,
    });

    if (error) {
      if (data.code === ORGANIZATION_UNAUTHORIZED) {
        return { error: 'ORGANIZATION_UNAUTHORIZED' };
      }

      if (data.code === USER_DEACTIVATED) {
        const lang = this.storeState.lang['WELCOME_SCREEN'];
        return { error: lang.USER_DEACTIVATED };
      }
      return { error: GENERIC_SERVER_ERROR_MESSAGE };
    }

    const { id, authType = AUTHENTICATION_TYPE.OTP } = data;
    // Attach user ID
    this.dispatch(attachUserDetails({ email }));
    this.dispatch(attachUserId({ id }));

    if (authType === AUTHENTICATION_TYPE.OTP && navigateToOtp) {
      // User have created an OTP
      navigate(ROUTES.OTP_VERIFICATION, { email });
    }

    return data;
  }

  /**
   * Contact us
   * @param {*} { email, fullName, phone, organization, details }
   * @returns
   * @memberof UserService
   */
  async contactUs({ email, fullName, organization, details }) {
    let { error, data } = await AuthenticationServiceApi.contactUs({
      email,
      fullName,
      organization,
      details,
    });

    if (error) {
      return { error: data.message };
    }

    return data;
  }

  /**
   * Verify OTP code and sign if needed
   * @param {*} { code }
   * @returns
   * @memberof UserService
   */
  async verifyCode({ code, userId }) {
    let { error, data } = await AuthenticationServiceApi.verifyCode({
      code,
      userId: userId || this.state.id,
    });

    if (error) {
      this.logger.warn(`Fail to verify code [${code}] ${data.message}`);
      return { error: data.message, code: data.code };
    }

    this.logger.info('Signin (verification code) - Succeeded', { userId: userId || this.state.id });
    // Data include token and user
    this.dispatch(signIn(data));

    return {};
  }

  /**
   * Signin with oauth
   * @param {*} { provider: String, codeVerifier: String, redirectUri:String, clientId: String, code: String }
   * @returns
   * @memberof UserService
   */
  async oauthLogin({ provider, codeVerifier, redirectUri, clientId, code }) {
    let { error, data } = await AuthenticationServiceApi.oauthVerification({
      provider,
      codeVerifier,
      redirectUri,
      clientId,
      code,
    });

    if (error) {
      if (data.code === ORGANIZATION_UNAUTHORIZED) {
        navigate(ROUTES.NOT_ALLOWED, { email: '' });
        return { error: '' };
      }
      return { error: GENERIC_SERVER_ERROR_MESSAGE };
    }

    this.logger.log('Signin (OAuth) - Succeeded', { clientId });
    // Data include token and user
    this.dispatch(signIn(data));

    return {};
  }

  /**
   * Activate oauth organization
   * @param {*} { provider: String, codeVerifier?: String, redirectUri:String, clientId: String, code: String }
   * @returns
   * @memberof UserService
   */

  async activateOauth({ provider, codeVerifier, redirectUri, clientId, code }) {
    let { error, data } = await AuthenticationServiceApi.activateOAuthAccount({
      provider,
      codeVerifier,
      redirectUri,
      clientId,
      code,
    });

    if (error) {
      if (data.code === ORGANIZATION_UNAUTHORIZED) {
        navigate(ROUTES.NOT_ALLOWED, { email: '' });
        return { error: '' };
      }
      return { error: GENERIC_SERVER_ERROR_MESSAGE };
    }

    this.dispatch(switchOrganization({ id: data.organizationId }));
    this.dispatch(activateAccount());

    return data;
  }

  /**
   *
   *
   * @returns
   * @memberof UserService
   */
  async init() {
    this.logger.info('Initializing authentication process', {
      userId: this.userId,
      organizationId: this.organizationId,
      refreshTokenDetails: this.state?.refreshTokenDetails,
      accessTokenDetails: this.state?.tokenDetails,
    });

    const isReady = await this._initByPlatform();

    if (isReady) {
      await this._userInit();
    }

    this._finishAuthStep();
  }

  async _initByPlatform() {
    if (global.platform === 'web') {
      return await this._webInit();
    }
    return await this._mobileInit();
  }

  async _userInit() {
    const { error, data } = await userServiceApi.me();

    if (error?.response?.status === 401) {
      this.logger.warn('getting user details failed, logging user out', {
        errorCode: error.response?.status,
      });
      this._forceLogout({ skipApiCall: true, signOutCode: SIGN_OUT_CODES.UNAUTHORIZED });
      return;
    }

    this.logger.info('user details fetched successfully - user is authenticated', {
      userId: data.user.id,
    });

    // Attach user details
    this.dispatch(attachUserDetails(data.user));

    // Fetch user's organization
    this.dispatch(updateOrganization());
  }

  async _mobileInit() {
    if (!this.isAuthenticated) {
      this.logger.info('user has no valid token');

      // Check if user has refresh token - if has, force local logout
      if (this.refreshToken) {
        this.logger.warn(
          'User about to logout locally - has refresh token, but not authenticated',
          {
            signOutCode: SIGN_OUT_CODES.SESSION_EXPIRED,
          },
        );

        this._forceLogout({ skipApiCall: true, signOutCode: SIGN_OUT_CODES.SESSION_EXPIRED });
      }

      const isOnboardingFinished = this.storeState.deviceFlags[DEVICE_FLAGS.ONBOARDING_FINISHED];
      if (!_.isUndefined(isOnboardingFinished) && !isOnboardingFinished) {
        navigate(ROUTES.ONBOARDING);
      }
      return false;
    }

    const isSuccessful = await this._refreshAccessTokenThatIsExpiringSoon();
    if (!isSuccessful) {
      this.logger.warn('failed to refresh access token - user logout locally', {
        signOutCode: SIGN_OUT_CODES.TOKEN_REFRESH_FAILED,
      });

      this.dispatch(
        logout({ skipApiCall: true, signOutCode: SIGN_OUT_CODES.TOKEN_REFRESH_FAILED }),
      );
    }
    return isSuccessful;
  }

  // In web, we use server side cookies for auth.
  // Because of that, the best way to test if the user is logged in is to refresh the access token
  async _webInit() {
    const authContext = await authenticationServiceApi.authContext();

    if (authContext.success) {
      this.dispatch(updateAuthenticationContext(authContext.data));
      return true;
    }

    this.logger.info('auth context request failed - will try to refresh token');

    const refreshSuccessful = await authService.refreshToken();
    if (!refreshSuccessful) {
      const signOutCode = this.state?.id ? SIGN_OUT_CODES.TOKEN_REFRESH_FAILED : undefined;

      this.logger.info('token refresh failed - user will be logged out', {
        signOutCode,
      });

      this.dispatch(logout({ skipApiCall: true, signOutCode }));
    }
    return refreshSuccessful;
  }

  /**
   * Select allowed region for authentication and further requests
   * @param {*} { email }
   * @private
   * @returns { region }
   * @memberof UserService
   */
  async _selectRegion({ email }) {
    const regionsAvailableResponse = await AuthenticationServiceApi.checkAvailabilityByRegion({
      email,
    });

    const index = regionsAvailableResponse.findIndex(({ data }) => data?.isAvailable);
    const error = regionsAvailableResponse.find(({ error }) => error)?.error;

    // handle error while trying checking region availability e.g. 429 - too many requests
    // doesn't throw an error in development mode to allow running only one region's backend service
    if (error && currentEnvironment() !== 'development') {
      throw new Error(GENERIC_SERVER_ERROR_MESSAGE);
    }

    const allowedRegion = API.getAPIRegions()[index];

    API.setClientByRegion(allowedRegion);

    return { region: allowedRegion };
  }

  /**
   * Handle access token refresh if token is expiring soon
   * @private
   * @memberof UserService
   */
  async _refreshAccessTokenThatIsExpiringSoon() {
    const hoursBeforeExpiration =
      this.storeState.generalConfig.HOURS_BEFORE_TOKEN_EXPIRATION_TO_REFRESH;

    // Check if token is expiring soon
    const tokenWillExpireSoon = this.tokenExpiration < hoursBeforeExpiration * 60 * 60 * 1000;

    this.logger.debug(`token expiration check`, {
      tokenWillExpireSoon,
      tokenExpiration: this.tokenExpiration,
      hoursBeforeExpiration: hoursBeforeExpiration,
      userId: this.userId,
      organizationId: this.organizationId,
      refreshTokenDetails: this.state?.refreshTokenDetails,
      accessTokenDetails: this.state?.tokenDetails,
    });

    // If token is not expiring soon, do nothing
    // Return true to indicate that token is not expiring soon
    if (!tokenWillExpireSoon) {
      return true;
    }

    this.logger.info(`token will expire`, {
      userId: this.userId,
      organizationId: this.organizationId,
    });

    const refreshSuccessful = await authService.refreshToken();

    if (refreshSuccessful) {
      this.logger.info('token refreshed successfully', {
        userId: this.userId,
        organizationId: this.organizationId,
      });
    }

    return refreshSuccessful;
  }

  _finishAuthStep() {
    this.dispatch(finishStep({ step: APPLICATION_STEPS.AUTHENTICATION_PROCESS }));
  }

  _forceLogout({ skipApiCall = false, signOutCode = null } = {}) {
    this.dispatch(logout({ skipApiCall, signOutCode }));
  }

  get userId() {
    return this.state.id || null;
  }

  get organizationId() {
    return this.state.user?.organizationId || null;
  }

  get region() {
    return this.state?.region || null;
  }

  // ========== Auth methods =============
  get accessToken() {
    let { accessToken } = this.state;

    return accessToken;
  }

  get tokenExpiration() {
    let { tokenDetails } = this.state;
    return 1000 * tokenDetails?.exp - Date.now();
  }

  get isTokenExpired() {
    let { tokenDetails } = this.state;
    if (tokenDetails && tokenDetails.exp) {
      return 1000 * tokenDetails.exp - Date.now() < 5000;
    }
    return true;
  }

  // Refresh token actions
  get refreshToken() {
    let { refreshToken } = this.state;

    return refreshToken;
  }

  get refreshTokenExpiration() {
    let { refreshTokenDetails } = this.state;
    return 1000 * refreshTokenDetails?.exp - Date.now();
  }

  get isRefreshTokenExpired() {
    let { refreshTokenDetails } = this.state;
    if (refreshTokenDetails && refreshTokenDetails.exp) {
      return 1000 * refreshTokenDetails.exp - Date.now() < 5000;
    }
    return this.isTokenExpired;
  }

  get isAuthenticated() {
    return !this.isRefreshTokenExpired;
  }
}

export default new UserService();
